import React, { useContext, useState } from 'react';
import { Field, Form } from 'react-final-form';

import { MDText } from 'i18n-react';

import { Button, ElectricIcon, LockClosedIcon } from '@motorway/mw-highway-code';
import { LoadingIndicator } from '@motorway/mw-highway-code/alpha';
import { VARIANT } from '@motorway/mw-highway-code/enums';
import { Input } from '@THC/components/Input';

import { postConsent } from 'PrivateAPI';

import { UserContext } from 'Context/user';

import { FSL_SNOW_PLOW_EVENTS } from 'Utilities/analytics/analyticsFinanceSettlementLetter';
import {
  numberValidator,
  simplePostCodeValidator,
} from 'Utilities/formValidators';

import { IconTextActionCard } from 'Storybook/components/local/IconTextActionCard/IconTextActionCard';
import { Modal } from 'Storybook/components/local/Modal/Modal';

import { FinanceSettlementProviderContext } from '../../DocumentCaptureContexts';

import LocalTexts from './FinanceProviderSection.texts.json';
import { useFsl } from './useFSL';

import styles from './FinanceProviderSection.scss';

type FinanceSettlementConsentModalProps = {
  enquiryId: string;
  provider: string;
  sellingVrm?: string;
  vrm: string;
};

const LocalT = new MDText(LocalTexts);

enum ModalScreen {
  Connecting = 'connecting',
  Consent = 'consent',
  Details = 'details',
}

type ModalScreenT = `${ModalScreen}`;

type ModalScreenProps = {
  closeModal: () => void;
  enquiryId: string;
  provider?: string;
  sellingVrm?: string;
  setScreen: React.Dispatch<React.SetStateAction<ModalScreenT>>;
  vrm: string;
};

const ConsentScreen: React.FC<ModalScreenProps> = (props) => {
  const { financeAgreement, setFinanceAgreement } = useContext(FinanceSettlementProviderContext);

  const [loading, setLoading] = useState(false);
  const handleSubmitConsent = async () => {
    setLoading(true);
    const payload = {
      enquiryId: props.enquiryId,
      status: 'granted',
    };
    await postConsent(payload);

    setLoading(false);
    props.setScreen(ModalScreen.Details);
    FSL_SNOW_PLOW_EVENTS.ACCEPT_CONTINUE_CONSENT_MODAL_CLICK();
  };

  const handleDeclineConsent = () => {
    if (setFinanceAgreement) {
      setFinanceAgreement({ ...financeAgreement, optedOut: true });
    }
    props.closeModal();
    FSL_SNOW_PLOW_EVENTS.DECLINE_CONSENT_MODAL_CLICK();
  };

  return (
    <div className={styles.consentScreen}>
      <h2 className={styles.title}>{LocalT.translate('modal.consent.title')}</h2>
      <p>{LocalT.translate('modal.consent.summary')}</p>
      <h4 className={styles.sharedDetailsTitle}>
        <LockClosedIcon />
        {LocalT.translate('modal.consent.sharedDetailsTitle')}
      </h4>
      <p>{LocalT.translate('modal.consent.sharedDetailsSummary')}</p>
      <p>{LocalT.translate('modal.consent.sharedDetailsListTitle')}</p>
      <ul>
        {LocalTexts.modal.consent.sharedDetailsListItems.map((item) => (
          <li key={item.replace(/\s/g, '')}>{LocalT.translate(item)}</li>
        ))}
      </ul>
      <br />
      <p className={styles.footer}>{LocalT.translate('modal.consent.footer')}</p>
      <br />
      <Button
        disabled={loading}
        icon="chevron"
        label={LocalT.translate('modal.consent.cta')}
        loading={loading}
        onClick={handleSubmitConsent}
        type="submit"
      />
      <Button
        disabled={loading}
        label={LocalT.translate('modal.consent.declineAction')}
        onClick={handleDeclineConsent}
        variant={VARIANT.SECONDARY}
      />
    </div>
  );
};

const ConnectingScreen = () => (
  <div className={styles.connectingScreen}>
    <div className={styles.loadingIcon}><LoadingIndicator size="large" /></div>
    <h2 className={styles.title}>{LocalT.translate('modal.connecting.title')}</h2>
    <p>{LocalT.translate('modal.connecting.summary')}</p>
  </div>
);

type DetailsValues = {
  agreementNumber: string;
  postcode: string;
}

const DetailsScreen: React.FC<ModalScreenProps> = (props) => {
  const { userState: user } = useContext(UserContext);
  const authToken = user?.auth_token;
  const { executePostSettlement, loading } = useFsl();
  const { financeAgreement, setFinanceAgreement } = useContext(FinanceSettlementProviderContext);

  const handleSubmitDetails = async (values: DetailsValues) => {
    const payload = {
      agreementNumber: values.agreementNumber,
      authToken,
      enquiryId: props.enquiryId,
      postcode: values.postcode,
      ...(props.sellingVrm && { sellingVrm: props.sellingVrm }),
      vrm: props.vrm,
    };
    props.setScreen(ModalScreen.Connecting);
    await executePostSettlement(payload).then((response) => {
      setFinanceAgreement?.(
        {
          ...financeAgreement,
          settlementDetails: {
            documentLocation: response.data?.location,
            expiry: response.data.expiry,
            figure: response.data.figure,
          },
        },
      );
      FSL_SNOW_PLOW_EVENTS.CONFIRM_DETAILS_CONTINUE_CLICK();
      props.closeModal();
    }).catch((error: Error) => {
      if (setFinanceAgreement) {
        const failureReason = error.message.includes('Finance agreement not found') ? 'agreement-not-found' : 'provider-service-down';
        setFinanceAgreement({ ...financeAgreement, failedLookupReason: failureReason });
      }
      FSL_SNOW_PLOW_EVENTS.LOOKUP_FAILURE();
      props.closeModal();
    });
  };

  return (
    <div className={styles.detailsScreen}>
      <h2 className={styles.title}>{LocalT.translate('modal.details.title')}</h2>
      <p className={styles.summary}>{LocalT.translate('modal.details.summary')}</p>
      {loading ? <div className={styles.loadingIcon}><LoadingIndicator size="large" /></div> : (
        <Form
          onSubmit={handleSubmitDetails}
          render={({ handleSubmit, invalid, submitting }) => (
            <form
              noValidate
              className={styles.inputContainer}
              onSubmit={handleSubmit}
            >
              <Field
                formatOnBlur
                component="input"
                name="agreementNumber"
                validate={numberValidator(
                  LocalT.translate('modal.details.formFields.financeAgreementNumber.errorMessage'),
                )}>
                {({ input, meta }) => (
                  <div className={styles.inputField}>
                    <label htmlFor="agreementNumber">
                      <span className={styles.labelTitle}>{LocalT.translate('modal.details.formFields.financeAgreementNumber.title')}</span>
                      <span className={styles.labelSummary}>{LocalT.translate('modal.details.formFields.financeAgreementNumber.summary')}</span>
                    </label>
                    <Input
                      data-thc-vrm-input
                      fullWidth
                      data-testid="agreementNumber"
                      formProps={{
                        input: {
                          ...input,
                          onChange: (event) => input.onChange(event),
                          onFocus: () => input.onFocus(),
                        },
                        meta,
                      }}
                      id="agreementNumber"
                      label={LocalT.translate(
                        'modal.details.formFields.financeAgreementNumber.title',
                      )}
                      placeholder={LocalT.translate(
                        'modal.details.formFields.financeAgreementNumber.placeholder',
                      )?.toString()}
                    />
                    {meta.touched && meta.error && (
                      <span className={styles.errorLabel}>{meta.error}</span>
                    )}
                  </div>
                )}
              </Field>
              <Field
                formatOnBlur
                component="input"
                name="postcode"
                validate={simplePostCodeValidator(
                  LocalT.translate('modal.details.formFields.postcode.errorMessage'),
                )}>
                {({ input, meta }) => (
                  <div className={styles.inputField}>
                    <label htmlFor="postcode">
                      <span className={styles.labelTitle}>{LocalT.translate('modal.details.formFields.postcode.title')}</span>
                      <span className={styles.labelSummary}>{LocalT.translate('modal.details.formFields.postcode.summary')}</span>
                    </label>
                    <Input
                      data-thc-vrm-input
                      fullWidth
                      data-testid="postcode"
                      formProps={{
                        input: {
                          ...input,
                          onChange: (event) => input.onChange(event),
                          onFocus: () => input.onFocus(),
                        },
                        meta,
                      }}
                      id="postcode"
                      label={LocalT.translate(
                        'modal.details.formFields.postcode.title',
                      )}
                      placeholder={LocalT.translate(
                        'modal.details.formFields.postcode.placeholder',
                      )?.toString()}
                    />
                    {meta.touched && meta.error && (
                      <span className={styles.errorLabel}>{meta.error}</span>
                    )}
                  </div>
                )}
              </Field>
              <br />
              <Button
                className={styles.btn}
                disabled={submitting || invalid}
                icon="chevron"
                label={LocalT.translate('modal.details.cta')}
                type="submit"
              />
            </form>
          )}
        />
      )}
    </div>
  );
};

export const ConnectFinanceProvider: React.FC<
  FinanceSettlementConsentModalProps
> = ({ enquiryId, provider, sellingVrm, vrm }) => {
  const [showModal, setShowModal] = useState(false);
  const [screen, setScreen] = useState<ModalScreenT>(ModalScreen.Consent);

  const handleOpenModal = () => {
    setShowModal(true);
    FSL_SNOW_PLOW_EVENTS.CONNECT_TO_FINANCE_PROVIDER_CLICK();
  };

  const handleCloseModal = () => {
    setScreen(ModalScreen.Consent);
    setShowModal(false);
    FSL_SNOW_PLOW_EVENTS.CLOSE_CONSENT_MODAL_CLICK();
  };

  const componentScreenMap: Partial<Record<ModalScreenT, React.JSX.Element>> = {
    [ModalScreen.Consent]: (
      <ConsentScreen
        closeModal={handleCloseModal}
        enquiryId={enquiryId}
        provider={provider}
        {...(sellingVrm && { sellingVrm })}
        setScreen={setScreen}
        vrm={vrm}
      />
    ),
    [ModalScreen.Details]: (
      <DetailsScreen closeModal={handleCloseModal}
        enquiryId={enquiryId}
        provider={provider}
        {...(sellingVrm && { sellingVrm })}
        setScreen={setScreen}
        vrm={vrm}
      />
    ),
    [ModalScreen.Connecting]: <ConnectingScreen />,
  };

  return (
    <>
      <IconTextActionCard
        icon={ElectricIcon}
        onClick={handleOpenModal}
        subtext={LocalT.translate('connectFinanceProviderText')}
        text={LocalT.translate('connectFinanceProviderTitle')}
      />

      {showModal && (
        <Modal
          centered
          className={styles.financeSettlementConsentModal}
          dismissModal={handleCloseModal}
          size="small"
        >
          <div className={styles.stickyWrapper}>
            {componentScreenMap[screen]}
          </div>
        </Modal>
      )}
    </>
  );
};
